/**
 * AdminAuditEventsPage — `/admin/audit-events`
 *
 * Admin-Audit-Browser dispatch. Read-only browser over the
 * `audit_events` collection (Phase 1d). Backend surface:
 *   - GET  /admin/audit-events                  (paginated list)
 *   - GET  /admin/audit-events/event-types      (dropdown source)
 *   - GET  /admin/audit-events/subject-types    (dropdown source)
 *
 * Layout:
 *   - Filter bar (event_type / subject_type / subject_id /
 *     actor_user_id / since / until + Apply + Clear)
 *   - Table (Timestamp / Event type / Actor / Subject)
 *   - Pagination controls (page size + prev/next + range indicator)
 *   - Row click → right-side Dialog with FULL metadata
 *
 * Timezone: timestamps render in the browser's IANA timezone via
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`. A `title=` on
 * each cell exposes the raw UTC-ISO for hover disambiguation.
 * Date-range inputs use HTML5 `datetime-local`; values are converted
 * to UTC-ISO before being sent to the backend (see
 * `_localInputToUtcIso`). The endpoint accepts naive strings as UTC
 * but we always send offset-aware — no ambiguity on the wire.
 *
 * Family L: only `--pp-*` brand tokens + existing utility classes.
 * No inline hex, no `prefers-color-scheme` refs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, RefreshCw, X } from "lucide-react";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];
const DEFAULT_PAGE_SIZE = 50;

// Reserved sentinel for the "Any" option in dropdowns. shadcn/ui Select
// rejects empty-string values (see components/ui/select.jsx contract).
const ANY = "__any__";

const _localTz = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_e) {
    return "UTC";
  }
})();

function _formatLocal(utcIso) {
  if (!utcIso) return "";
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return utcIso;
  // en-CA yields "YYYY-MM-DD, HH:mm:ss" — strip the comma to match spec.
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: _localTz,
  })
    .format(d)
    .replace(",", "");
}

function _localInputToUtcIso(localVal) {
  // `datetime-local` inputs return "YYYY-MM-DDTHH:mm" (browser-local
  // clock). `new Date(...)` interprets that as local time; toISOString
  // yields the UTC form.
  if (!localVal) return "";
  const d = new Date(localVal);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

export default function AdminAuditEventsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [eventType, setEventType] = useState(ANY);
  const [subjectType, setSubjectType] = useState(ANY);
  const [subjectId, setSubjectId] = useState("");
  const [actorUserId, setActorUserId] = useState("");
  const [sinceLocal, setSinceLocal] = useState("");
  const [untilLocal, setUntilLocal] = useState("");

  // Pagination
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  // Dropdown options (fetched from helper endpoints)
  const [eventTypeOpts, setEventTypeOpts] = useState([]);
  const [subjectTypeOpts, setSubjectTypeOpts] = useState([]);

  // Detail drawer
  const [openRow, setOpenRow] = useState(null);

  // "Applied" filter snapshot — the values that actually gate the
  // current API call. Kept separate from the input-bound state so
  // typing in the boxes doesn't refetch on every keystroke.
  const appliedRef = useRef({
    eventType: ANY,
    subjectType: ANY,
    subjectId: "",
    actorUserId: "",
    since: "",
    until: "",
  });
  const [appliedTick, setAppliedTick] = useState(0);

  // Fetch the two helper dropdowns once on mount.
  useEffect(() => {
    (async () => {
      try {
        const [et, st] = await Promise.all([
          api.get("/admin/audit-events/event-types"),
          api.get("/admin/audit-events/subject-types"),
        ]);
        setEventTypeOpts(et.data.values || []);
        setSubjectTypeOpts(st.data.values || []);
      } catch (e) {
        // Dropdowns are a UX nicety — a failure here still lets the
        // page function via the free-text filters.
        // eslint-disable-next-line no-console
        console.warn("audit-events: helper dropdowns failed to load", e);
      }
    })();
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        limit: pageSize,
        offset,
      };
      const a = appliedRef.current;
      if (a.eventType && a.eventType !== ANY) params.event_type = a.eventType;
      if (a.subjectType && a.subjectType !== ANY) params.subject_type = a.subjectType;
      if (a.subjectId) params.subject_id = a.subjectId;
      if (a.actorUserId) params.actor_user_id = a.actorUserId;
      if (a.since) params.since = _localInputToUtcIso(a.since);
      if (a.until) params.until = _localInputToUtcIso(a.until);
      const { data } = await api.get("/admin/audit-events", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = formatApiErrorDetail(detail) || e.message || "Failed to load audit events";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [pageSize, offset]);

  // Re-fetch whenever offset / pageSize change OR the user hits Apply.
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, offset, appliedTick]);

  const handleApply = () => {
    appliedRef.current = {
      eventType,
      subjectType,
      subjectId: subjectId.trim(),
      actorUserId: actorUserId.trim(),
      since: sinceLocal,
      until: untilLocal,
    };
    setOffset(0);
    setAppliedTick((t) => t + 1);
  };

  const handleClear = () => {
    setEventType(ANY);
    setSubjectType(ANY);
    setSubjectId("");
    setActorUserId("");
    setSinceLocal("");
    setUntilLocal("");
    appliedRef.current = {
      eventType: ANY,
      subjectType: ANY,
      subjectId: "",
      actorUserId: "",
      since: "",
      until: "",
    };
    setOffset(0);
    setAppliedTick((t) => t + 1);
  };

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (total === 0) return { rangeStart: 0, rangeEnd: 0 };
    return { rangeStart: offset + 1, rangeEnd: Math.min(offset + items.length, total) };
  }, [offset, items.length, total]);

  const hasPrev = offset > 0;
  const hasNext = offset + items.length < total;

  return (
    <div className="space-y-6" data-testid="admin-audit-events-page">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-pp-text">Audit events</h1>
          <p className="mt-1 text-sm text-pp-text-muted">
            Immutable, append-only browser over the <code>audit_events</code> collection.
            Timestamps shown in your local timezone (<span data-testid="audit-tz-label">{_localTz}</span>); hover for UTC.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => { setAppliedTick((t) => t + 1); }}
          disabled={loading}
          data-testid="audit-refresh-button"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </header>

      {/* -------------------------- Filter bar ------------------------- */}
      <section
        className="grid grid-cols-1 gap-4 rounded-2xl border border-pp-border bg-pp-surface p-5 md:grid-cols-3"
        data-testid="audit-filter-bar"
      >
        <div>
          <Label htmlFor="audit-filter-event-type" className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Event type</Label>
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger id="audit-filter-event-type" data-testid="audit-filter-event-type">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} data-testid="audit-filter-event-type-any">Any</SelectItem>
              {eventTypeOpts.map((v) => (
                <SelectItem key={v} value={v} data-testid={`audit-filter-event-type-${v}`}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="audit-filter-subject-type" className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Subject type</Label>
          <Select value={subjectType} onValueChange={setSubjectType}>
            <SelectTrigger id="audit-filter-subject-type" data-testid="audit-filter-subject-type">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} data-testid="audit-filter-subject-type-any">Any</SelectItem>
              {subjectTypeOpts.map((v) => (
                <SelectItem key={v} value={v} data-testid={`audit-filter-subject-type-${v}`}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="audit-filter-subject-id" className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Subject ID</Label>
          <Input
            id="audit-filter-subject-id"
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            placeholder="exact match"
            data-testid="audit-filter-subject-id"
          />
        </div>
        <div>
          <Label htmlFor="audit-filter-actor-user-id" className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Actor user ID</Label>
          <Input
            id="audit-filter-actor-user-id"
            value={actorUserId}
            onChange={(e) => setActorUserId(e.target.value)}
            placeholder="exact match"
            data-testid="audit-filter-actor-user-id"
          />
        </div>
        <div>
          <Label htmlFor="audit-filter-since" className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Since (local)</Label>
          <Input
            id="audit-filter-since"
            type="datetime-local"
            value={sinceLocal}
            onChange={(e) => setSinceLocal(e.target.value)}
            data-testid="audit-filter-since"
          />
        </div>
        <div>
          <Label htmlFor="audit-filter-until" className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Until (local, exclusive)</Label>
          <Input
            id="audit-filter-until"
            type="datetime-local"
            value={untilLocal}
            onChange={(e) => setUntilLocal(e.target.value)}
            data-testid="audit-filter-until"
          />
        </div>
        <div className="md:col-span-3 flex flex-wrap gap-3">
          <Button
            type="button"
            onClick={handleApply}
            disabled={loading}
            data-testid="audit-filter-apply"
          >
            Apply
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleClear}
            disabled={loading}
            data-testid="audit-filter-clear"
          >
            Clear all
          </Button>
        </div>
      </section>

      {/* --------------------------- Table ---------------------------- */}
      <section className="rounded-2xl border border-pp-border bg-pp-surface" data-testid="audit-table-wrap">
        {error ? (
          <div className="p-8 text-center" data-testid="audit-error-state">
            <p className="text-sm font-semibold text-pp-text">Couldn't load audit events.</p>
            <p className="mt-1 text-sm text-pp-text-muted">{error}</p>
            <Button
              type="button"
              className="mt-4"
              onClick={fetchList}
              data-testid="audit-error-retry"
            >
              Retry
            </Button>
          </div>
        ) : loading ? (
          <div className="p-8 text-center" data-testid="audit-loading-state">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-pp-text-muted" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center" data-testid="audit-empty-state">
            <p className="text-sm font-semibold text-pp-text">
              No audit events match the current filters.
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              onClick={handleClear}
              data-testid="audit-empty-clear"
            >
              Clear all filters
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-pp-border text-left text-xs uppercase tracking-wider text-pp-text-muted">
                  <th className="px-4 py-3">Timestamp</th>
                  <th className="px-4 py-3">Event type</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Subject</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer border-b border-pp-border last:border-0 hover:bg-pp-surface2"
                    onClick={() => setOpenRow(row)}
                    data-testid={`audit-row-${row.id}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-pp-text" title={row.created_at} data-testid={`audit-row-${row.id}-timestamp`}>
                      {_formatLocal(row.created_at)}
                    </td>
                    <td className="px-4 py-3 text-pp-text" data-testid={`audit-row-${row.id}-event-type`}>
                      {row.event_type}
                    </td>
                    <td className="px-4 py-3 text-pp-text-muted" data-testid={`audit-row-${row.id}-actor`}>
                      <span className="font-mono text-xs">{row.actor_user_id}</span>
                      <span className="ml-2 rounded-full border border-pp-border px-2 py-0.5 text-[10px] uppercase tracking-wider">{row.actor_role}</span>
                    </td>
                    <td className="px-4 py-3 text-pp-text-muted" data-testid={`audit-row-${row.id}-subject`}>
                      <span className="font-mono text-xs">{row.subject_type}:{row.subject_id}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* -------------------- Pagination controls --------------------- */}
      <section className="flex flex-wrap items-center justify-between gap-3" data-testid="audit-pagination">
        <div className="flex items-center gap-3">
          <Label htmlFor="audit-page-size" className="text-xs text-pp-text-muted">Page size</Label>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => { setPageSize(Number(v)); setOffset(0); }}
          >
            <SelectTrigger id="audit-page-size" className="w-24" data-testid="audit-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`audit-page-size-${n}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-pp-text-muted" data-testid="audit-pagination-range">
            {total === 0 ? "No results" : `Showing ${rangeStart}-${rangeEnd} of ${total}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOffset(Math.max(0, offset - pageSize))}
            disabled={!hasPrev || loading}
            data-testid="audit-pagination-prev"
          >
            Prev
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOffset(offset + pageSize)}
            disabled={!hasNext || loading}
            data-testid="audit-pagination-next"
          >
            Next
          </Button>
        </div>
      </section>

      {/* ------------------------- Detail drawer ---------------------- */}
      <Dialog open={!!openRow} onOpenChange={(o) => { if (!o) setOpenRow(null); }}>
        <DialogContent className="max-w-2xl" data-testid="audit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-pp-text">Audit event</DialogTitle>
          </DialogHeader>
          {openRow && (
            <div className="space-y-4">
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-[max-content_1fr]" data-testid="audit-detail-fields">
                <dt className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">ID</dt>
                <dd className="font-mono text-xs text-pp-text" data-testid="audit-detail-id">{openRow.id}</dd>
                <dt className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Event type</dt>
                <dd className="text-sm text-pp-text" data-testid="audit-detail-event-type">{openRow.event_type}</dd>
                <dt className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Timestamp (local)</dt>
                <dd className="font-mono text-xs text-pp-text" data-testid="audit-detail-ts-local">{_formatLocal(openRow.created_at)}</dd>
                <dt className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Timestamp (UTC)</dt>
                <dd className="font-mono text-xs text-pp-text-muted" data-testid="audit-detail-ts-utc">{openRow.created_at}</dd>
                <dt className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Actor</dt>
                <dd className="text-sm text-pp-text" data-testid="audit-detail-actor">
                  <span className="font-mono text-xs">{openRow.actor_user_id}</span>
                  <span className="ml-2 rounded-full border border-pp-border px-2 py-0.5 text-[10px] uppercase tracking-wider">{openRow.actor_role}</span>
                </dd>
                <dt className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Subject</dt>
                <dd className="text-sm text-pp-text" data-testid="audit-detail-subject">
                  <span className="font-mono text-xs">{openRow.subject_type}:{openRow.subject_id}</span>
                </dd>
                <dt className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">IP</dt>
                <dd className="font-mono text-xs text-pp-text" data-testid="audit-detail-ip">{openRow.ip || "—"}</dd>
                <dt className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">User agent</dt>
                <dd className="text-xs text-pp-text-muted" data-testid="audit-detail-ua">{openRow.user_agent || "—"}</dd>
              </dl>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">Metadata</div>
                <pre
                  className="mt-2 max-h-80 overflow-auto rounded-xl border border-pp-border bg-pp-surface2 p-3 font-mono text-xs text-pp-text"
                  data-testid="audit-detail-metadata"
                >
                  {JSON.stringify(openRow.metadata || {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpenRow(null)}
              data-testid="audit-detail-close"
            >
              <X className="mr-2 h-4 w-4" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
